import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import MapIcon from '@mui/icons-material/Map';
import { Box, Container, Divider, Fab, Fade, IconButton, Tooltip, Typography, Slider, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import Grid from '@mui/material/Grid2';
import React, { useState, useEffect } from 'react';
import "../../styles/events.css"; // Import CSS
import { theme } from '../../styles/theme';
import { cardGridSize } from '../../utils/constants';
import { AppBarPadding, BottomPadding } from '../../utils/helperUtils';
import ExploreMapView from '../components/ExploreMapView';
import SearchComponent from './components/SearchComponent';
import SearchEventsVendorCardComponent from './components/SearchEventsVendorCardComponent';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { fetchEventListingsNearby } from '../../services/eventServices';
import LoadingComponent from '../components/LoadingComponent';

function ExploreEventsView({ maxCardsToShow = 10 }) {
  const [searchQuery, setSearchQuery] = useState("");

  const [listView, setListView] = useState(true)

  const [filterOpen, setFilterOpen] = useState(false);
  const [miles, setMiles] = useState(25);

  const [eventListings, setEventListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mapLandingLocation, setMapLandingLocation] = useState(null);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const getLocation = () =>
          new Promise((resolve, reject) => {
            if (navigator.geolocation) {
              navigator.geolocation.getCurrentPosition(
                (pos) =>
                  resolve({
                    lat: pos.coords.latitude,
                    lng: pos.coords.longitude,
                  }),
                (err) => reject(err)
              );
            } else {
              reject(new Error('Geolocation not supported'));
            }
          });

        const { lat, lng } = await getLocation();
        const response = await fetchEventListingsNearby({ miles, lat, lng });

        if (response.success) {
          setEventListings(response.data);
        } else {
          setEventListings([]);
        }
      } catch (err) {
        console.error('Failed to fetch nearby events:', err);
        setError(err.message)

        setEventListings([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const filteredEvents = eventListings.filter(event =>
    event.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSearchArea = async (coords) => {
    try {
      setLoading(true);
      const response = await fetchEventListingsNearby({ miles, lat: coords.lat, lng: coords.lng });
      if (response.success) {
        setEventListings(response.data);
      } else {
        setEventListings([]);
      }
      setMapLandingLocation(coords);
    } catch (err) {
      console.error("Search in area failed:", err);
      setError(err.message || "Search failed");
      setEventListings([]);
    } finally {
      setLoading(false);
    }
  };

  const FloatingActionButton = () => {
    const handleClick = (e) => {
      e?.preventDefault();
      setListView((listView) => !listView)
      // navigate("/_/events/organizer/create");

    };

    return (
      <Tooltip title="Create New Event" arrow>
        <Fab
          variant="extended"
          // color={theme.colors.events.secondary}
          onClick={(e) => handleClick(e)}
          sx={{
            position: "fixed",
            bottom: 20,
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: theme.colors.events.secondary,
            color: theme.colors.events.primaryDark,
            fontWeight: "bold",
            "&:hover": {
              backgroundColor: theme.colors.events.primary,
            },
          }}
        >
          {listView ? <MapIcon sx={{ mr: 1 }} /> : <FormatListBulletedIcon sx={{ mr: 1 }} />}
          {listView ? "Map" : "List"} View
        </Fab>
      </Tooltip>
    );
  };

  return (
    <Box bgcolor={theme.colors.events.primary} >
      <Container maxWidth="xl" className="events-box-container">
        <AppBarPadding />
        <Grid container justifyContent={'center'}>
          <Grid size={{ md: 12, lg: 6, xs: 12 }}>
            <Typography fontSize={{ xs: 25, md: 30 }} color={theme.colors.events.secondary}>
              <Box sx={{ fontWeight: 'bold', mb: 1 }}>
                Search Nearby Events 🔍
              </Box>
            </Typography>
            <Divider sx={{ borderBottomWidth: 2, bgcolor: theme.colors.events.secondary }} />
            {/* 🔍 Search Bar */}
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              sx={{
                width: '100%',         // takes full width of container
                maxWidth: '100%',      // prevents overflow
                overflow: 'hidden'     // clips if any child misbehaves
              }}
            >
              <Box flexGrow={1} minWidth={0}>
                <SearchComponent
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  onSearchClick={() => console.log("Searching for:", searchQuery)}
                />
              </Box>
              <IconButton
                size="small"
                onClick={() => setFilterOpen(true)}
                sx={{ color: theme.colors.events.secondary }}
              >
                <FilterAltOutlinedIcon />
              </IconButton>
            </Box>
            {
              error && (
                <Box textAlign="center" mt={4} className="box-container">
                  <Typography color="error" variant="h6">⚠️ {error || 'Something went wrong, please try again later'}</Typography>
                </Box>
              )
            }
            {loading ? (
              <LoadingComponent fullScreen={false} />
            ) :
              <Box
                sx={{
                  position: 'relative',
                  height: 400, // set a fixed height to prevent layout shift
                  mt: 2,
                }}
              >
                {/* List View */}
                <Fade in={listView} timeout={400} unmountOnExit>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '70vh',
                      overflowY: 'auto',
                    }}
                  >
                    {filteredEvents.length === 0 && (
                      <Box textAlign="center" mt={4}>
                        <Typography color="white" variant="h6">
                          🙁 No events found in this area. Try adjusting your search or filter settings
                        </Typography>
                      </Box>
                    )}
                    <Grid container spacing={2}>
                      {filteredEvents.slice(0, maxCardsToShow).map((event) => (
                        <Grid key={event.event_listing_id} size={cardGridSize}>
                          <SearchEventsVendorCardComponent event={event} />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Fade>

                {/* Map View */}
                <Fade in={!listView} timeout={400} unmountOnExit>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                    }}
                  >
                    <ExploreMapView
                      markers={filteredEvents.map((event) => ({
                        lat: event.location.coordinates[1],
                        lng: event.location.coordinates[0],
                        label: event.name,
                        slug: event.slug
                      }))}
                      onSearchArea={handleSearchArea}
                      landingLocation={mapLandingLocation}
                    />
                  </Box>
                </Fade>
              </Box>
            }
          </Grid>
        </Grid>
        <BottomPadding />
        <FloatingActionButton />
        <Dialog open={filterOpen} onClose={() => setFilterOpen(false)} fullWidth maxWidth="xs">
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h6" fontWeight={600}>
                Your Search Radius
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            <Typography fontSize={14} color="text.secondary" gutterBottom>
              Filter events based on how far you're willing to travel.
            </Typography>
            <Slider
              value={miles}
              onChange={(e, newValue) => setMiles(newValue)}
              min={1}
              max={50}
              valueLabelDisplay="on"
              sx={{
                mt: 2,
                color: theme.colors.events.primary,
                '& .MuiSlider-thumb': {
                  borderRadius: 2,
                },
              }}
            />
            <Box display="flex" justifyContent="center" alignItems="center" mt={0}>
              <Typography variant="body2" color="text.secondary">
                Current Radius: <strong>{miles} miles</strong>
              </Typography>
              {/* <Button
                onClick={() => setMiles(25)}
                size="small"
                variant="text"
                color="secondary"
                sx={{
                  textTransform: 'none',
                  borderRadius: 3,
                  fontWeight: 500,
                }}
              >
                Reset
              </Button> */}
            </Box>
          </DialogContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" px={3} pb={2}>
            <Button
              onClick={() => setFilterOpen(false)}
              sx={{ textTransform: 'none', borderRadius: 3 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => setFilterOpen(false)}
              sx={{
                backgroundColor: theme.colors.events.primary,
                color: 'white',
                textTransform: 'none',
                borderRadius: 3,
                fontWeight: 600,
                px: 3,
              }}
            >
              Apply
            </Button>
          </Box>
        </Dialog>
      </Container>
    </Box>
  )
}

export default ExploreEventsView